import React, { FC, useState } from 'react'
import { Grid } from '@mui/material'
import { useParams } from 'react-router-dom'
import { HasSidebarLayout, DataLoad, EntityHeader, CollapsibleSection, SensorUploadInfo, SensorUploadOrders, SensorUploadSensors } from 'src/components'
import { useSensorUploadRequest } from 'src/hooks/api'
import { useAddPopupMessage } from 'src/hooks/ui'
import { SensorUpload } from 'src/api'

// Same-tab anchor click: popup blockers ignore it (unlike window.open/_blank after an await),
// and presigned zip/xlsx links trigger a download instead of navigating away
const openLink = (link: string): void => {
  const anchor = document.createElement('a')
  anchor.href = link
  anchor.download = ''
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}

const SensorUploadShow: FC = () => {
  document.title = 'CLīMIT Admin Panel - Sensors Upload'
  const params = useParams()
  const id = parseInt((params as { uploadId: string }).uploadId, 10)
  const addMessage = useAddPopupMessage()
  const [isDownloading, setIsDownloading] = useState(false)
  const { data, isInitialLoading, isError, error, refetch } = useSensorUploadRequest({ id })

  // Presigned links (source file and QR archives) expire in 10 minutes,
  // so every download re-fetches the details to get a fresh link
  const downloadFreshLink = async (pickLink: (upload: SensorUpload) => string | undefined): Promise<void> => {
    setIsDownloading(true)
    try {
      const { data: fresh } = await refetch()
      const link = fresh ? pickLink(fresh) : undefined
      if (link) openLink(link)
      else addMessage({ text: 'The download link is not available', type: 'error' })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDownloadSourceFile = () => downloadFreshLink((upload) => upload.file?.link)
  const handleDownloadArchive = (archiveId: number) =>
    downloadFreshLink((upload) => (upload.orders || []).flatMap((order) => order.archives || []).find((archive) => archive.id === archiveId)?.link)

  return (
    <HasSidebarLayout>
      <DataLoad isLoading={isInitialLoading} isError={isError} errorMessage={error?.message}>
        {data ? (
          <Grid container>
            <Grid item xs={12}>
              <EntityHeader title={`Sensors upload #${data.id}`} />
            </Grid>
            <Grid item xs={12}>
              <SensorUploadInfo upload={data} onDownloadSourceFile={handleDownloadSourceFile} isDownloading={isDownloading} />
            </Grid>
            <Grid item xs={12} mt={2}>
              <CollapsibleSection title="Orders" defaultExpanded>
                <SensorUploadOrders orders={data.orders || []} onDownloadArchive={handleDownloadArchive} />
              </CollapsibleSection>
            </Grid>
            <Grid item xs={12}>
              <CollapsibleSection title="Imported Sensors">
                <SensorUploadSensors uploadId={id} type="imported" />
              </CollapsibleSection>
            </Grid>
            <Grid item xs={12}>
              <CollapsibleSection title="Skipped Sensors">
                <SensorUploadSensors uploadId={id} type="skipped" />
              </CollapsibleSection>
            </Grid>
          </Grid>
        ) : null}
      </DataLoad>
    </HasSidebarLayout>
  )
}

export default SensorUploadShow
